import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import shaka from 'shaka-player';
import { usePlayerContext } from '../PlayerContext';
import { Dropdowns } from '../types';
import { Dropdown } from './Dropdown';

type DropdownControllerProps = {
  active: Dropdowns;
  onHide(): void;
};

export type Option<T> = {
  label?: string;
  value: T;
};

// Playback speed dropdown
const speedOptions = [
  { value: 0.25 },
  { value: 0.5 },
  { value: 0.75 },
  { value: 1 },
  { value: 1.25 },
  { value: 1.5 },
  { value: 2 },
];

export const DropdownController: FC<DropdownControllerProps> = ({ active, onHide }) => {
  const { t } = useTranslation();
  const { player, getVideoOptions, getAudioOptions, getSubtitleOptions, getActiveTrack, isAbr } = usePlayerContext();

  const variants = getVideoOptions();
  const audio = getAudioOptions();
  const subtitles = getSubtitleOptions();
  const activeVariant = getActiveTrack();

  const setPlaybackRate = (speed: number) => {
    if (speed === 1) {
      player?.cancelTrickPlay();
    } else {
      player?.trickPlay(speed);
    }
    onHide();
  };

  // Subtitle track dropdown
  const getTrackDisplayName = (track: shaka.extern.Track) =>
    `${track.language} ${track.label ? `(${track.label})` : ''}`;

  const selectTrack = (value: number) => {
    if (value === -1) {
      player?.setTextTrackVisibility(false);
    } else {
      const index = Number(value);
      player?.setTextTrackVisibility(true);
      player?.selectTextTrack(subtitles[index]);
    }
    onHide();
  };

  // Variant dropdown
  const selectVariant = (value: string | number) => {
    if (value === 'auto') {
      // Enable ABR
      player?.configure({ abr: { enabled: true } });
    } else {
      const newId = Number(value);
      // Disable ABR
      player?.configure({ abr: { enabled: false } });
      // Select track
      const newTrack = variants.find(({ id }) => id === newId);
      if (newTrack) {
        player?.selectVariantTrack(newTrack);
      }
    }
    onHide();
  };

  const selectAudio = (value: string) => {
    player?.selectAudioLanguage(value);
  };

  switch (active) {
    case 'hidden':
      return null;
    case 'speed':
      return (
        <Dropdown
          title={t('player.controls.playbackSpeed.title')}
          options={speedOptions}
          currentValue={player?.getPlaybackRate() || 1}
          onSelect={setPlaybackRate}
        />
      );
    case 'subtitles':
      return (
        <Dropdown
          title={t('player.controls.subtitles.title')}
          options={[
            { value: -1, label: t('player.controls.subtitles.empty') },
            ...subtitles.map(track => ({ value: track.id, label: getTrackDisplayName(track) })),
          ]}
          currentValue={subtitles.find(({ active }) => active)?.id ?? -1}
          onSelect={selectTrack}
        />
      );
    case 'languages':
      return (
        <Dropdown
          title={t('player.controls.language.title')}
          options={audio.map(a => ({
            value: a,
          }))}
          currentValue={activeVariant?.language ?? ''}
          onSelect={selectAudio}
        />
      );
    case 'variants':
      return (
        <Dropdown
          title={t('player.controls.quality.title')}
          options={[
            ...variants
              .sort((variant1, variant2) => (variant2.width ?? 0) - (variant1.width ?? 0))
              .map(variant => ({ value: variant.id, label: `${variant.width}x${variant.height}` })),
            { value: 'auto', label: t('player.controls.quality.auto') },
          ]}
          currentValue={isAbr() ? 'auto' : activeVariant?.id ?? -1}
          onSelect={selectVariant}
        />
      );
  }
};
